import React from 'react';
import ReactMarkdown from 'react-markdown';
import SearchHeader from '../components/SearchHeader';
import TopNav from '../components/TopNav';
import { darkPurple, accent } from '../utils/colors';
import { fetchSkillById, updateSkill } from '../utils/api';

export default function SkillEditPage({ apiBase }) {
  const [skill, setSkill] = React.useState(null);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [departmentId, setDepartmentId] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [editMode, setEditMode] = React.useState(false);

  const searchInputRef = React.useRef();
  const [search, setSearch] = React.useState('');
  const [searchDropdown, setSearchDropdown] = React.useState(false);

  React.useEffect(() => {
    const hash = (window.location.hash || '').replace('#/skill/', '');
    const id = parseInt(hash, 10);
    if (!apiBase || !id) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchSkillById(apiBase, id);
        setSkill(data);
        setTitle(data.title || '');
        setDescription(data.description || '');
        setDepartmentId(data.departmentId);
        setEditMode(false);
      } catch (e) {
        setError(e.message || 'Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase]);

  const save = async () => {
    if (!skill) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        id: skill.id,
        parentId: skill.parentId,
        title,
        description,
        departmentId,
        level: skill.level,
        orderNum: skill.orderNum,
        hasChildren: skill.hasChildren,
      };
      await updateSkill(apiBase, payload);
      setSkill({ ...skill, title: payload.title, description: payload.description, departmentId: payload.departmentId });
      setEditMode(false);
    } catch (e) {
      setError(e.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    if (!skill) return;
    setTitle(skill.title || '');
    setDescription(skill.description || '');
    setDepartmentId(skill.departmentId || 0);

    setEditMode(false);
  };

  return (
    <div className="App" style={{ background: darkPurple, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SearchHeader
        search={search}
        setSearch={setSearch}
        searchInputRef={searchInputRef}
        setSearchDropdown={setSearchDropdown}
        searchDropdown={searchDropdown}
      />
      <TopNav current="matrix" />
      <div style={{ padding: 12 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 4px 24px #a259f733' }}>
          {loading ? (
            <div style={{ color: darkPurple }}>Загрузка...</div>
          ) : error ? (
            <div style={{ color: 'red' }}>{error}</div>
          ) : !skill ? (
            <div style={{ color: '#888' }}>Навык не найден</div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h2 style={{ color: accent, margin: 0 }}>{skill.title}</h2>
                <button onClick={() => setEditMode(e => !e)} disabled={saving} style={{ background: editMode ? '#eee' : accent, color: editMode ? accent : '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>{editMode ? 'Отмена' : 'Редактировать'}</button>
              </div>

              {editMode ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                    <label style={{ fontWeight: 600 }}>Название</label>
                    <input value={title} onChange={e => setTitle(e.target.value)} style={{ border: `1.5px solid ${accent}`, borderRadius: 8, padding: '8px 10px' }} />
                    <label style={{ fontWeight: 600 }}>Department ID</label>
                    <input type="number" value={departmentId !== undefined && departmentId !== null ? departmentId : ''} onChange={e => setDepartmentId(Number(e.target.value))} style={{ border: `1.5px solid ${accent}`, borderRadius: 8, padding: '8px 10px' }} />
                    <label style={{ fontWeight: 600 }}>Описание</label>
                    <textarea value={description} onChange={e => setDescription(e.target.value)} rows={8} style={{ border: `1.5px solid ${accent}`, borderRadius: 8, padding: '8px 10px' }} />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <button onClick={save} disabled={saving} style={{ background: accent, color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 16, fontWeight: 600, cursor: 'pointer', marginRight: 10 }}>{saving ? 'Сохраняю...' : 'Сохранить'}</button>
                    <button onClick={cancel} disabled={saving} style={{ background: '#eee', color: accent, border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 16, fontWeight: 600, cursor: 'pointer' }}>Отмена</button>
                  </div>
                </>
              ) : (
                <div className="markdown-block">
                  <ReactMarkdown
                    components={{
                      h1: ({children}) => <h1 style={{color: accent, fontSize: '1.8em', marginTop: '1.5em', marginBottom: '0.5em', fontWeight: 600}}>{children}</h1>,
                      h2: ({children}) => <h2 style={{color: accent, fontSize: '1.5em', marginTop: '1.5em', marginBottom: '0.5em', fontWeight: 600}}>{children}</h2>,
                      h3: ({children}) => <h3 style={{color: accent, fontSize: '1.3em', marginTop: '1.5em', marginBottom: '0.5em', fontWeight: 600}}>{children}</h3>,
                      p: ({children}) => <p style={{marginBottom: '1em'}}>{children}</p>,
                      ul: ({children}) => <ul style={{marginBottom: '1em', paddingLeft: '2em'}}>{children}</ul>,
                      ol: ({children}) => <ol style={{marginBottom: '1em', paddingLeft: '2em'}}>{children}</ol>,
                      li: ({children}) => <li style={{marginBottom: '0.5em'}}>{children}</li>,
                      code: ({children}) => <code style={{background: '#f3eaff', padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace', fontSize: '0.9em'}}>{children}</code>,
                      pre: ({children}) => <pre style={{background: '#f3eaff', padding: '1em', borderRadius: 8, overflow: 'auto', marginBottom: '1em'}}>{children}</pre>,
                      blockquote: ({children}) => <blockquote style={{borderLeft: `4px solid ${accent}`, paddingLeft: '1em', marginLeft: 0, color: '#6a1b9a', fontStyle: 'italic'}}>{children}</blockquote>,
                      strong: ({children}) => <strong style={{fontWeight: 600, color: accent}}>{children}</strong>,
                      em: ({children}) => <em style={{fontStyle: 'italic'}}>{children}</em>,
                      a: ({children, href}) => <a href={href} style={{color: accent, textDecoration: 'none'}} target="_blank" rel="noopener noreferrer">{children}</a>
                    }}
                  >
                    {(skill.description || '').replace(/\r\n|\r/g, '\n').replace(/^[ \t]+/gm, '')}
                  </ReactMarkdown>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

